import React, { useEffect, useState, useRef, useMemo } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { ENTITY } from '../../../redux/slices/timeline';

interface BirdsEyeViewProps {
  startDate?: string;
  endDate?: string;
  viewType: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  containerId?: string;
}

const BirdsEyeView: React.FC<BirdsEyeViewProps> = ({
  startDate,
  endDate,
  viewType,
  containerId = 'timeline-container'
}) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [totalWidth, setTotalWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const birdsEyeRef = useRef<HTMLDivElement>(null);

  const { currentEntity, simulation } = useSelector((state: any) => state.timeline);
  const { data: tasks, singleRoadmapRes } = useSelector((state: any) => state.roadmap);
  // Minimap dimensions
  const miniWidth = 220; // px
  const miniHeight = 32; // px

  const timelineStartDate = useMemo(() => {
    if (startDate) return startDate;
    if (currentEntity === ENTITY.ROADMAP) {
      if (simulation) return singleRoadmapRes?.sim_data?.start_date;
      return singleRoadmapRes?.start_date;
    }
    if (currentEntity === ENTITY.PROJECT) {
      return tasks?.[0]?.start;
    }
    return moment().format('YYYY-MM-DD');
  }, [startDate, currentEntity, simulation, singleRoadmapRes, tasks]);

  const timelineEndDate = useMemo(() => {
    if (endDate) return endDate;
    if (currentEntity === ENTITY.ROADMAP) {
      if (simulation) return singleRoadmapRes?.sim_data?.end_date;
      return singleRoadmapRes?.end_date;
    }
    if (currentEntity === ENTITY.PROJECT) {
      return tasks?.[0]?.end;
    }
    return moment().add(1, 'year').format('YYYY-MM-DD');
  }, [endDate, currentEntity, simulation, singleRoadmapRes, tasks]);

  const visibleDates = useMemo(() => {
    if (!timelineStartDate || !timelineEndDate) return [] as any[];
    const dates: any[] = [];
    const start = moment(timelineStartDate);
    const end = moment(timelineEndDate);
    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      dates.push({ date: current.format('YYYY-MM-DD'), nextDayIsHoliday: false });
      switch (viewType) {
        case 'daily':
          current.add(1, 'day');
          break;
        case 'weekly':
          current.add(1, 'week');
          break;
        case 'monthly':
          current.add(1, 'month');
          break;
        case 'quarterly':
          current.add(3, 'months');
          break;
        case 'yearly':
          current.add(1, 'year');
          break;
        default:
          current.add(1, 'day');
      }
    }
    return dates;
  }, [timelineStartDate, timelineEndDate, viewType]);

  const scale = totalWidth > 0 ? miniWidth / totalWidth : 1;
  const indicatorWidth = Math.max(Math.min(containerWidth * scale, miniWidth), 16);
  const indicatorLeft = Math.max(0, Math.min(miniWidth - indicatorWidth, scrollPosition * scale));

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const updateScrollInfo = () => {
      setScrollPosition(container.scrollLeft);
      setContainerWidth(container.clientWidth);
      setTotalWidth(container.scrollWidth);
    };

    // Initial measurement
    updateScrollInfo();

    // Add scroll listener
    container.addEventListener('scroll', updateScrollInfo);
    window.addEventListener('resize', updateScrollInfo);

    return () => {
      container.removeEventListener('scroll', updateScrollInfo);
      window.removeEventListener('resize', updateScrollInfo);
    };
  }, [containerId, visibleDates.length]);

  const handleBirdsEyeClick = (e: React.MouseEvent) => {
    const container = document.getElementById(containerId);
    if (!container || !birdsEyeRef.current) return;

    const rect = birdsEyeRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const centeredX = Math.max(0, Math.min(miniWidth, clickX - indicatorWidth / 2));
    const targetScrollLeft = (centeredX / scale);
    container.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });
  };

  const handleIndicatorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const updateScrollFromClientX = (clientX: number) => {
    const container = document.getElementById(containerId);
    if (!container || !birdsEyeRef.current) return;

    const rect = birdsEyeRef.current.getBoundingClientRect();
    const localX = clientX - rect.left;
    const clamped = Math.max(0, Math.min(miniWidth - indicatorWidth, localX - indicatorWidth / 2));
    const targetScrollLeft = clamped / scale;
    container.scrollLeft = targetScrollLeft;
  };

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    updateScrollFromClientX(e.clientX);
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!isDragging) return;
      updateScrollFromClientX(e.clientX);
    };
    const handleUp = () => setIsDragging(false);
    if (isDragging) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging, scale, indicatorWidth]);

  if (!timelineStartDate || !timelineEndDate || visibleDates.length === 0) {
    return null;
  }

  return (
    <div className="absolute left-4 bottom-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 p-2">
        <div
          ref={birdsEyeRef}
          className="relative bg-gray-100 dark:bg-gray-700 rounded cursor-pointer overflow-hidden border-0 p-0"
          onClick={handleBirdsEyeClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleBirdsEyeClick(e as any);
            }
          }}
          aria-label="Timeline overview - click to navigate to timeline position"
          role="button"
          tabIndex={0}
          style={{ width: `${miniWidth}px`, height: `${miniHeight}px` }}
        >
          {/* Timeline segments */}
          <div className="absolute inset-0 flex">
            {visibleDates.map((date, index) => {
              const segmentWidth = miniWidth / visibleDates.length;
              const isWeekend = moment(date.date).day() === 0 || moment(date.date).day() === 6;
              const isHoliday = date.nextDayIsHoliday;
              
              let segmentClass = 'h-full border-r border-gray-300 dark:border-gray-600 ';
              if (isHoliday) {
                segmentClass += 'bg-red-100 dark:bg-red-900/30';
              } else if (isWeekend) {
                segmentClass += 'bg-gray-200 dark:bg-gray-600';
              } else {
                segmentClass += 'bg-white dark:bg-gray-700';
              }
              
              return (
                <div
                  key={date.date}
                  className={segmentClass}
                  style={{ width: `${segmentWidth}px` }}
                />
              );
            })}
          </div>


          {/* Visible area indicator */}
          <div
            className="absolute top-0 h-full bg-transparent border-2 border-blue-500 rounded cursor-grab active:cursor-grabbing z-20 p-0"
            style={{
              left: `${indicatorLeft}px`,
              width: `${indicatorWidth}px`
            }}
            onClick={handleIndicatorClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleIndicatorClick(e as any);
              }
            }}
            aria-label="Current visible area - drag to adjust view"
            onMouseDown={handleDragStart}
            role="button"
            tabIndex={0}
          >
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirdsEyeView;
